// frontend/src/components/SyntaxHighlightedEditor.tsx
import { useRef, useCallback, useLayoutEffect } from 'react';
import Prism from 'prismjs';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-markdown';

interface SyntaxHighlightedEditorProps {
  value: string;
  onChange: (value: string) => void;
  language: 'python' | 'html' | 'css' | 'javascript' | 'markdown' | 'text';
  readOnly?: boolean;
  placeholder?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

// Prism tokenization on multi-megabyte files can freeze the editor.
// For large payloads, render plain escaped text instead.
const MAX_HIGHLIGHT_CHARS = 200_000;

// Map our language names to Prism language keys
const languageMap: Record<string, string> = {
  python: 'python',
  html: 'markup',
  css: 'css',
  javascript: 'javascript',
  markdown: 'markdown',
  text: 'markup',
};

// Escape HTML entities for safe insertion
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export default function SyntaxHighlightedEditor({
  value,
  onChange,
  language,
  readOnly = false,
  placeholder = '',
  onKeyDown,
}: SyntaxHighlightedEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const preRef = useRef<HTMLPreElement>(null);
  // Track the desired scroll position so we can restore it after React re-renders
  const scrollTopRef = useRef(0);
  const scrollLeftRef = useRef(0);

  // Sync scroll positions: textarea -> pre, and save for restoration
  const syncScroll = useCallback(() => {
    if (textareaRef.current && preRef.current) {
      scrollTopRef.current = textareaRef.current.scrollTop;
      scrollLeftRef.current = textareaRef.current.scrollLeft;
      preRef.current.scrollTop = textareaRef.current.scrollTop;
      preRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  }, []);

  // Use useLayoutEffect for synchronous scroll binding
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.addEventListener('scroll', syncScroll, { passive: true });
      return () => textarea.removeEventListener('scroll', syncScroll);
    }
  }, [syncScroll]);

  // CRITICAL: Use useLayoutEffect to restore scroll positions synchronously
  useLayoutEffect(() => {
    if (textareaRef.current && preRef.current) {
      textareaRef.current.scrollTop = scrollTopRef.current;
      textareaRef.current.scrollLeft = scrollLeftRef.current;
      preRef.current.scrollTop = scrollTopRef.current;
      preRef.current.scrollLeft = scrollLeftRef.current;
    }
  }, [value]);

  // Handle tab key for indentation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = textareaRef.current;
      if (textarea && !readOnly) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newValue = value.substring(0, start) + '  ' + value.substring(end);
        onChange(newValue);
        // Set cursor position after indent
        requestAnimationFrame(() => {
          textarea.selectionStart = textarea.selectionEnd = start + 2;
        });
      }
    }
    onKeyDown?.(e);
  };

  // Get highlighted HTML using Prism
  const getHighlightedCode = (): string => {
    if (!value) {
      if (placeholder) {
        return '<span class="editor-placeholder">' + escapeHtml(placeholder) + '</span>';
      }
      return '\n';
    }

    let highlighted: string;

    if (value.length > MAX_HIGHLIGHT_CHARS) {
      highlighted = escapeHtml(value);
    } else {
      const prismLang = languageMap[language] || 'markup';
      const grammar = Prism.languages[prismLang];
      if (!grammar) {
        highlighted = escapeHtml(value);
      } else {
        highlighted = Prism.highlight(value, grammar, prismLang);
      }
    }

    if (!highlighted) {
      highlighted = escapeHtml(value);
    }

    // CRITICAL FIX: Only append a newline if the content actually ends with one.
    // Browsers ignore a trailing newline in a <pre> block (it doesn't create a new visual line),
    // whereas <textarea> creates an empty line for the cursor.
    // By appending an extra newline to the <pre> ONLY when the text implies it, we match the 
    // textarea's rendering behavior exactly.
    if (value.endsWith('\n')) {
      highlighted += '\n';
    }

    return highlighted;
  };

  return (
    <div className="code-editor-container">
      {/* Highlighted code display (background layer) */}
      <pre
        ref={preRef}
        aria-hidden="true"
      >
        <code dangerouslySetInnerHTML={{ __html: getHighlightedCode() }} />
      </pre>

      {/* Invisible textarea for input (foreground layer) */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => !readOnly && onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onScroll={syncScroll}
        readOnly={readOnly}
        spellCheck={false}
        autoCapitalize="off"
        autoComplete="off"
        autoCorrect="off"
        data-gramm="false"
        data-gramm_editor="false"
        data-enable-grammarly="false"
        placeholder=""
        className={readOnly ? 'cursor-default' : ''}
      />
    </div>
  );
}
